/**
 * 상세 수수료율 설정 페이지 유틸리티 함수들
 * legacy detailFeeRateSetting.js에서 포팅한 비즈니스 로직
 */

// ===== 타입 정의 =====

export interface RateRowUi {
  id: string;
  startMonth: string; // YYYYMM 형식
  endMonth: string; // YYYYMM 형식
  rate: number;
  isApplied: boolean;
  rangeApplied: boolean;
}

export interface DrugGroupUi {
  id: string;
  drugId: number | null;
  pharmaCompanyId: number | null;
  name: string;
  code: string;
  fixedEnabled: boolean;
  rows: RateRowUi[];
  isCustomAdded?: boolean;
}

export interface PharmaGroupUi {
  id: string;
  pharmaCompanyId: number | null;
  name: string;
  fixedEnabled: boolean;
  rows: RateRowUi[];
}

export interface DetailDataUi {
  pharmaGroups: PharmaGroupUi[];
  selectedPharmaId: string | undefined;
  drugGroupsByPharmaId: Record<string, DrugGroupUi[]>;
}

export interface BusinessRelationshipItem {
  clinicId: number;
  clinicName: string;
  clinicType: 'HOSPITAL' | 'CLINIC';
  pharmaCompanyId?: number;
  pharmaCompanyName?: string;
  drugId?: number;
  drugName?: string;
  drugCode?: string;
  drugIds?: (number | string)[];
  pharmaCompanies?: Array<{ pharmaCompanyId: number }>;
  drugApprovalStatus?: string;
  transactionStatus?: string;
  detailFeeConfigured?: boolean;
}

// ===== 날짜 관련 헬퍼 =====

/**
 * YYYYMM 문자열에 개월 수를 더한다
 */
export const addMonthsYm = (ym: string, months: number): string => {
  if (!/^\d{6}$/.test(ym)) return ym;
  
  const year = parseInt(ym.slice(0, 4), 10);
  const month = parseInt(ym.slice(4, 6), 10);
  
  const date = new Date(year, month - 1 + months, 1);
  const newYear = date.getFullYear();
  const newMonth = date.getMonth() + 1;
  
  return `${newYear}${newMonth.toString().padStart(2, '0')}`;
};

/**
 * YYYYMM 형식으로 포맷
 */
export const formatYm = (value?: string | Date | null): string => {
  if (!value) return '';
  
  if (typeof value === 'string') {
    // 이미 YYYYMM 형식인지 확인
    if (/^\d{6}$/.test(value)) return value;
    // YYYY-MM 형식이면 변환
    if (/^\d{4}-\d{2}$/.test(value)) {
      return value.replace('-', '');
    }
  }
  
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return `${year}${month.toString().padStart(2, '0')}`;
};

/**
 * YYYY-MM 형식으로 포맷 (API 요청용)
 */
export const formatYmWithDash = (value?: string | Date | null): string => {
  const ym = formatYm(value);
  if (!ym || ym.length !== 6) return '';
  return `${ym.slice(0, 4)}-${ym.slice(4, 6)}`;
};

/**
 * 종료 연월 API 요청값.
 * 서버는 종료 연월을 선택값(null 허용)으로 받지만 YYYY-MM 패턴을 검사하므로,
 * 빈 문자열을 보내면 '잘못된 요청 파라미터' 400이 난다. 미설정은 null로 보낸다.
 */
export const toEndYearMonthPayload = (value?: string | null): string | null =>
  formatYmWithDash(value) || null;

/**
 * 현재 월부터 다음 월까지의 기본 수수료 행 생성
 */
export const createCurrentToNextMonthRateRow = (): RateRowUi[] => {
  const now = new Date();
  const currentYm = formatYm(now);
  const nextYm = addMonthsYm(currentYm, 1);
  
  return [
    {
      id: `${Date.now()}-r-init`,
      startMonth: currentYm,
      endMonth: nextYm,
      rate: 0,
      isApplied: true,
      rangeApplied: false,
    },
  ];
};

/**
 * 기존 행들 다음 연속된 월의 수수료 행 생성
 */
export const createNextSequentialRateRow = (rows: RateRowUi[] = []): RateRowUi => {
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
  const lastEndMonth = lastRow?.endMonth || formatYm(new Date());
  const nextStartMonth = addMonthsYm(lastEndMonth, 1);
  const nextEndMonth = addMonthsYm(nextStartMonth, 1);
  
  return {
    id: `${Date.now()}-${Math.random()}`,
    startMonth: nextStartMonth,
    endMonth: nextEndMonth,
    rate: 0,
    isApplied: true,
    rangeApplied: false,
  };
};

// ===== ID 해결 함수들 =====

/**
 * 아이템에서 drugId 추출
 */
export const resolveDrugId = (item: any): number | null => {
  const toValidNumber = (value: any) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  if (item?.drugId != null) return toValidNumber(item.drugId);
  if (item?.id != null) return toValidNumber(item.id);

  const rawDrugIds = item?.drugIds;
  if (Array.isArray(rawDrugIds)) {
    const first = rawDrugIds.find(
      (value) => value != null && `${value}`.trim(),
    );
    return first != null ? toValidNumber(first) : null;
  }

  if (typeof rawDrugIds === 'string') {
    const parts = rawDrugIds
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length > 0) return toValidNumber(parts[0]);
  }

  return null;
};

/**
 * 아이템에서 pharmaCompanyId 추출
 */
export const resolvePharmaCompanyId = (item: any): number | null => {
  if (item?.pharmaCompanyId != null) {
    const parsed = Number(item.pharmaCompanyId);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * 아이템에서 pharmaCompanyIds 배열 추출
 */
export const resolvePharmaCompanyIds = (item: any): number[] => {
  if (Array.isArray(item?.pharmaCompanies) && item.pharmaCompanies.length > 0) {
    return item.pharmaCompanies
      .map((entry: any) => Number(entry?.pharmaCompanyId))
      .filter((value: any) => Number.isFinite(value));
  }

  if (item?.pharmaCompanyId != null) {
    const parsed = Number(item.pharmaCompanyId);
    return Number.isFinite(parsed) ? [parsed] : [];
  }

  return [];
};

// ===== 데이터 빌딩 함수들 =====

/**
 * 관계 데이터에서 의약품 그룹들 생성
 */
export const buildDrugRows = (rows: BusinessRelationshipItem[], pharmaId: string): DrugGroupUi[] => {
  const unique: DrugGroupUi[] = [];
  const seen = new Set<string>();

  rows.forEach((item, index) => {
    const code = item.drugCode || `no-code-${index}`;
    const pharmaCompanyId = resolvePharmaCompanyId(item);
    const key = `${pharmaCompanyId || 'no-pharma'}-${code}-${item.drugName || ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    
    unique.push({
      id: `${pharmaId}-${code}-${index}`,
      drugId: resolveDrugId(item),
      pharmaCompanyId: pharmaCompanyId != null ? Number(pharmaCompanyId) : null,
      name: item.drugName || '-',
      code: item.drugCode || '-',
      fixedEnabled: false,
      rows: [],
    });
  });

  return unique;
};

const EMPTY_DETAIL_DATA: DetailDataUi = {
  pharmaGroups: [],
  selectedPharmaId: undefined,
  drugGroupsByPharmaId: {},
};

/**
 * 비즈니스 관계 데이터로부터 상세 데이터 구조 생성
 */
export const buildDetailDataByRelationships = ({
  relationships,
  isHospitalScenario,
}: {
  relationships: BusinessRelationshipItem[];
  isHospitalScenario: boolean;
}): DetailDataUi => {
  if (!Array.isArray(relationships) || relationships.length === 0) {
    return EMPTY_DETAIL_DATA;
  }

  const pharmaMap = new Map<string, PharmaGroupUi>();
  const pharmaRowsMap: Record<string, BusinessRelationshipItem[]> = {};

  relationships.forEach((item) => {
    const pharmaCompanyId = resolvePharmaCompanyId(item);
    const pharmaCompanyName = item.pharmaCompanyName || '-';
    const pharmaKey = pharmaCompanyId != null ? String(pharmaCompanyId) : pharmaCompanyName;
    
    if (!pharmaMap.has(pharmaKey)) {
      pharmaMap.set(pharmaKey, {
        id: `pharma-${pharmaKey}`,
        pharmaCompanyId,
        name: pharmaCompanyName,
        fixedEnabled: false,
        rows: [],
      });
    }
    
    if (!pharmaRowsMap[pharmaKey]) {
      pharmaRowsMap[pharmaKey] = [];
    }
    pharmaRowsMap[pharmaKey].push(item);
  });

  let pharmaGroups = Array.from(pharmaMap.values());
  
  // 의원급인 경우 승인된 제약사만 필터링
  if (!isHospitalScenario) {
    pharmaGroups = pharmaGroups.filter((group) =>
      (pharmaRowsMap[
        group.pharmaCompanyId != null
          ? String(group.pharmaCompanyId)
          : group.name
      ] || []).some((item) => item.transactionStatus === '승인')
    );
  }

  const drugSourceRows = isHospitalScenario
    ? relationships.filter((item) => item.drugApprovalStatus === 'APPROVED')
    : relationships;

  const drugGroupsByPharmaId: Record<string, DrugGroupUi[]> = {};
  
  pharmaGroups.forEach((group) => {
    const pharmaKey = group.pharmaCompanyId != null
      ? String(group.pharmaCompanyId)
      : group.name;
      
    if (isHospitalScenario) {
      const rows = (pharmaRowsMap[pharmaKey] || []).filter(
        (item) => item.drugApprovalStatus === 'APPROVED'
      );
      drugGroupsByPharmaId[group.id] = buildDrugRows(rows, group.id);
      return;
    }
    
    drugGroupsByPharmaId[group.id] = [];
  });

  if (isHospitalScenario) {
    drugGroupsByPharmaId['all-pharma'] = buildDrugRows(drugSourceRows, 'all-pharma');
  }

  return {
    pharmaGroups,
    selectedPharmaId: isHospitalScenario ? 'all-pharma' : undefined,
    drugGroupsByPharmaId,
  };
};

// ===== 스냅샷 및 더티 검사 =====

/**
 * 두 의약품 그룹이 동일한지 확인
 */
export const isSameDrugGroup = (left: DrugGroupUi, right: DrugGroupUi): boolean => {
  if (left?.drugId != null && right?.drugId != null) {
    return Number(left.drugId) === Number(right.drugId);
  }
  return String(left?.code || '') === String(right?.code || '');
};

/**
 * 더티 체크용 스냅샷 생성
 */
export const buildDetailDataSnapshot = (data: DetailDataUi): string => {
  const normalizeRows = (rows: RateRowUi[] = []) =>
    [...rows]
      .map((row) => ({
        startMonth: row.startMonth || '',
        endMonth: row.endMonth || '',
        rate: Number(row.rate || 0),
        isApplied: Boolean(row.isApplied),
        rangeApplied: Boolean(row.rangeApplied),
      }))
      .sort((left, right) => 
        left.startMonth.localeCompare(right.startMonth) ||
        left.endMonth.localeCompare(right.endMonth) ||
        left.rate - right.rate ||
        Number(left.isApplied) - Number(right.isApplied) ||
        Number(left.rangeApplied) - Number(right.rangeApplied)
      );

  const normalizeDrugGroups = (groups: DrugGroupUi[] = []) =>
    [...groups]
      .map((group) => ({
        drugId: group?.drugId != null ? Number(group.drugId) : null,
        pharmaCompanyId: group?.pharmaCompanyId != null ? Number(group.pharmaCompanyId) : null,
        name: String(group?.name || ''),
        code: String(group?.code || ''),
        fixedEnabled: Boolean(group?.fixedEnabled),
        rows: normalizeRows(group?.rows || []),
      }))
      .sort((left, right) =>
        (left.pharmaCompanyId || 0) - (right.pharmaCompanyId || 0) ||
        (left.drugId || 0) - (right.drugId || 0) ||
        left.code.localeCompare(right.code) ||
        left.name.localeCompare(right.name)
      );

  return JSON.stringify({
    pharmaGroups: [...(data?.pharmaGroups || [])]
      .map((group) => ({
        pharmaCompanyId: group?.pharmaCompanyId != null ? Number(group.pharmaCompanyId) : null,
        name: String(group?.name || ''),
        fixedEnabled: Boolean(group?.fixedEnabled),
        rows: normalizeRows(group?.rows || []),
      }))
      .sort((left, right) =>
        (left.pharmaCompanyId || 0) - (right.pharmaCompanyId || 0) ||
        left.name.localeCompare(right.name)
      ),
    drugGroupsByPharmaId: Object.fromEntries(
      Object.entries(data?.drugGroupsByPharmaId || {})
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([pharmaId, groups]) => [pharmaId, normalizeDrugGroups(groups)])
    ),
  });
};

// ===== 병원 시나리오 동기화 함수들 =====

/**
 * 병원 시나리오: 전체 제약사 의약품을 각 개별 제약사로 동기화
 */
export const syncHospitalAllPharmaDrugsToEachPharma = (
  data: DetailDataUi,
  isHospitalScenario: boolean,
): DetailDataUi => {
  if (!isHospitalScenario) return data;
  
  const allPharmaDrugs = data?.drugGroupsByPharmaId?.['all-pharma'];
  if (!Array.isArray(allPharmaDrugs)) return data;

  const pharmaIdByCompanyId = new Map<number, string>(
    (data.pharmaGroups || [])
      .filter((group) => group.pharmaCompanyId != null)
      .map((group) => [Number(group.pharmaCompanyId), group.id])
  );

  const nextDrugGroupsByPharmaId = { ...(data.drugGroupsByPharmaId || {}) };

  allPharmaDrugs.forEach((allDrug) => {
    const pharmaCompanyId = allDrug?.pharmaCompanyId != null
      ? Number(allDrug.pharmaCompanyId)
      : null;
    const targetPharmaId = pharmaCompanyId != null
      ? pharmaIdByCompanyId.get(pharmaCompanyId)
      : null;
    if (!targetPharmaId) return;

    const targetGroups = Array.isArray(nextDrugGroupsByPharmaId[targetPharmaId])
      ? [...nextDrugGroupsByPharmaId[targetPharmaId]]
      : [];
    const targetIndex = targetGroups.findIndex((drug) => isSameDrugGroup(drug, allDrug));
    if (targetIndex < 0) return;

    targetGroups[targetIndex] = {
      ...targetGroups[targetIndex],
      fixedEnabled: Boolean(allDrug.fixedEnabled),
      rows: Array.isArray(allDrug.rows)
        ? allDrug.rows.map((row) => ({ ...row }))
        : [],
    };
    nextDrugGroupsByPharmaId[targetPharmaId] = targetGroups;
  });

  return {
    ...data,
    drugGroupsByPharmaId: nextDrugGroupsByPharmaId,
  };
};

/**
 * 병원 시나리오: 선택된 제약사 의약품을 전체 제약사로 동기화
 */
export const syncHospitalSelectedPharmaDrugsToAllPharma = (
  data: DetailDataUi,
  selectedPharmaId: string,
  isHospitalScenario: boolean,
): DetailDataUi => {
  if (!isHospitalScenario || selectedPharmaId === 'all-pharma') return data;

  const selectedGroups = data?.drugGroupsByPharmaId?.[selectedPharmaId];
  const allPharmaGroups = data?.drugGroupsByPharmaId?.['all-pharma'];
  if (!Array.isArray(selectedGroups) || !Array.isArray(allPharmaGroups)) {
    return data;
  }

  const selectedPharmaGroup = (data.pharmaGroups || []).find(
    (group) => group.id === selectedPharmaId
  );
  const selectedPharmaCompanyId = selectedPharmaGroup?.pharmaCompanyId;
  if (selectedPharmaCompanyId == null) return data;

  const nextAllPharmaGroups = allPharmaGroups.map((group) => {
    const groupPharmaCompanyId = group?.pharmaCompanyId != null ? Number(group.pharmaCompanyId) : null;
    if (groupPharmaCompanyId !== selectedPharmaCompanyId) {
      return group;
    }

    const selectedGroup = selectedGroups.find((item) => isSameDrugGroup(item, group));
    if (!selectedGroup) return group;

    return {
      ...group,
      fixedEnabled: Boolean(selectedGroup.fixedEnabled),
      rows: Array.isArray(selectedGroup.rows)
        ? selectedGroup.rows.map((row) => ({ ...row }))
        : [],
    };
  });

  return {
    ...data,
    drugGroupsByPharmaId: {
      ...(data.drugGroupsByPharmaId || {}),
      'all-pharma': nextAllPharmaGroups,
    },
  };
};

// ===== 서버 데이터 병합 =====

interface ServerPharmaRate {
  pharmaCompanyId?: number;
  startYearMonth?: string;
  endYearMonth?: string;
  rate?: number;
  isApplied?: boolean;
  isRangeApplied?: boolean;
}

interface ServerDrugRate {
  pharmaCompanyId?: number;
  drugId?: number;
  drugCode?: string;
  drugName?: string;
  startYearMonth?: string;
  endYearMonth?: string;
  rate?: number;
  isApplied?: boolean;
  isRangeApplied?: boolean;
  pharmaCompanies?: Array<{ pharmaCompanyId: number }>;
}

const resolveIsApplied = (item: any): boolean =>
  item?.isApplied == null ? true : Boolean(item.isApplied);

const resolveRangeApplied = (item: any): boolean =>
  Boolean(item?.isRangeApplied);

const toYm = (value?: string): string => {
  if (!value) return '';
  // YYYY-MM -> YYYYMM 변환
  if (/^\d{4}-\d{2}$/.test(value)) {
    return value.replace('-', '');
  }
  return value;
};

/**
 * 서버에서 받은 수수료 데이터를 클라이언트 데이터에 병합
 */
export const mergeDetailRatesFromServer = (
  baseData: DetailDataUi,
  serverData: {
    clinicPharmaRates?: ServerPharmaRate[];
    hospitalDrugRules?: ServerDrugRate[];
    clinicDrugRates?: ServerDrugRate[];
  },
  isHospitalScenario: boolean,
): DetailDataUi => {
  const next: DetailDataUi = {
    ...baseData,
    pharmaGroups: [...baseData.pharmaGroups],
    drugGroupsByPharmaId: Object.fromEntries(
      Object.entries(baseData.drugGroupsByPharmaId || {}).map(([key, value]) => [
        key,
        Array.isArray(value) ? [...value] : value,
      ])
    ),
  };

  // 제약사 수수료 병합
  const pharmaRateRows = Array.isArray(serverData.clinicPharmaRates) 
    ? serverData.clinicPharmaRates 
    : [];

  if (pharmaRateRows.length > 0) {
    const groupedByPharmaId = pharmaRateRows.reduce((acc: Record<string, ServerPharmaRate[]>, item) => {
      const pharmaId = item?.pharmaCompanyId != null ? Number(item.pharmaCompanyId) : null;
      const key = pharmaId != null ? String(pharmaId) : '__UNKNOWN__';
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    next.pharmaGroups = next.pharmaGroups.map((group) => {
      const key = group.pharmaCompanyId != null
        ? String(Number(group.pharmaCompanyId))
        : '__UNKNOWN__';
      const rows = groupedByPharmaId[key] || [];
      if (rows.length === 0) return group;
      
      return {
        ...group,
        fixedEnabled: rows.some((row) => resolveIsApplied(row)),
        rows: rows.map((row, index) => ({
          id: `${group.id}-pharma-row-${index}`,
          startMonth: toYm(row.startYearMonth),
          endMonth: toYm(row.endYearMonth),
          rate: Number(row.rate || 0),
          isApplied: resolveIsApplied(row),
          rangeApplied: resolveRangeApplied(row),
        })),
      };
    });
  }

  // 의약품 수수료 병합
  const drugRateRows = isHospitalScenario
    ? Array.isArray(serverData.hospitalDrugRules) ? serverData.hospitalDrugRules : []
    : Array.isArray(serverData.clinicDrugRates) ? serverData.clinicDrugRates : [];

  if (drugRateRows.length > 0) {
    const pharmaGroupMap = new Map(
      next.pharmaGroups.map((group) => [
        group.pharmaCompanyId != null
          ? String(Number(group.pharmaCompanyId))
          : '__UNKNOWN__',
        group,
      ])
    );

    drugRateRows.forEach((row, index) => {
      const pharmaCompanyIds = resolvePharmaCompanyIds(row);
      const scopedCompanyIds = pharmaCompanyIds.length > 0 ? pharmaCompanyIds : [null];

      scopedCompanyIds.forEach((pharmaCompanyId) => {
        const pharmaKey = pharmaCompanyId != null ? String(pharmaCompanyId) : null;
        const pharmaGroup = pharmaKey ? pharmaGroupMap.get(pharmaKey) : null;
        const targetPharmaId = isHospitalScenario
          ? 'all-pharma'
          : pharmaGroup?.id || null;
        if (!targetPharmaId) return;

        const existingGroups = next.drugGroupsByPharmaId[targetPharmaId] || [];
        const targetDrugId = row?.drugId != null ? Number(row.drugId) : null;
        const targetDrugCode = String(row?.drugCode || '').trim();

        let targetDrugGroup = existingGroups.find((item) => {
          if (
            isHospitalScenario &&
            targetPharmaId === 'all-pharma' &&
            pharmaCompanyId != null
          ) {
            const itemPharmaCompanyId = item?.pharmaCompanyId != null
              ? Number(item.pharmaCompanyId)
              : null;
            if (itemPharmaCompanyId !== pharmaCompanyId) {
              return false;
            }
          }
          if (targetDrugId != null && item?.drugId != null) {
            return Number(item.drugId) === targetDrugId;
          }
          if (targetDrugCode) {
            return String(item?.code || '').trim() === targetDrugCode;
          }
          return false;
        });

        if (!targetDrugGroup) {
          targetDrugGroup = {
            id: `${targetPharmaId}-loaded-${targetDrugId || targetDrugCode || index}`,
            drugId: targetDrugId,
            pharmaCompanyId: pharmaCompanyId,
            name: row.drugName || '-',
            code: row.drugCode || '-',
            fixedEnabled: false,
            rows: [],
          };
          existingGroups.push(targetDrugGroup);
        }

        targetDrugGroup.rows = [
          ...(targetDrugGroup.rows || []),
          {
            id: `${targetDrugGroup.id}-row-${index}`,
            startMonth: toYm(row.startYearMonth),
            endMonth: toYm(row.endYearMonth),
            rate: Number(row.rate || 0),
            isApplied: resolveIsApplied(row),
            rangeApplied: resolveRangeApplied(row),
          },
        ];
        targetDrugGroup.fixedEnabled =
          targetDrugGroup.fixedEnabled || resolveIsApplied(row);
      });
    });

    next.drugGroupsByPharmaId = { ...next.drugGroupsByPharmaId };
    Object.keys(next.drugGroupsByPharmaId).forEach((pharmaId) => {
      const groups = next.drugGroupsByPharmaId[pharmaId];
      if (!Array.isArray(groups)) return;
      next.drugGroupsByPharmaId[pharmaId] = groups.map((group) => ({
        ...group,
        rows: Array.isArray(group.rows) ? group.rows : [],
      }));
    });
  }

  return next;
};

// ===== 페이로드 빌더 =====

interface NormalizePharmaRate {
  pharmaCompanyId: number | null;
  rate: number;
  isApplied: boolean;
  isRangeApplied: boolean;
  startYearMonth: string | null;
  endYearMonth: string | null;
}

interface NormalizeDrugRate {
  pharmaCompanyId: number | null;
  drugId: number | null;
  rate: number;
  isApplied: boolean;
  isRangeApplied: boolean;
  startYearMonth: string | null;
  endYearMonth: string | null;
}

export const normalizePharmaRate = (item: any): NormalizePharmaRate => ({
  pharmaCompanyId: item?.pharmaCompanyId != null ? Number(item.pharmaCompanyId) : null,
  rate: Number(item?.rate ?? 0),
  isApplied: resolveIsApplied(item),
  isRangeApplied: resolveRangeApplied(item),
  startYearMonth: item?.startYearMonth || null,
  endYearMonth: item?.endYearMonth || null,
});

export const normalizeDrugRate = (item: any): NormalizeDrugRate => ({
  pharmaCompanyId: item?.pharmaCompanyId != null
    ? Number(item.pharmaCompanyId)
    : Array.isArray(item?.pharmaCompanies) && item.pharmaCompanies[0]?.pharmaCompanyId != null
      ? Number(item.pharmaCompanies[0].pharmaCompanyId)
      : null,
  drugId: item?.drugId != null ? Number(item.drugId) : null,
  rate: Number(item?.rate ?? 0),
  isApplied: resolveIsApplied(item),
  isRangeApplied: resolveRangeApplied(item),
  startYearMonth: item?.startYearMonth || null,
  endYearMonth: item?.endYearMonth || null,
});

export const areRatesEqual = <T>(
  left: T[] = [], 
  right: T[] = [], 
  normalizer: (item: T) => any
): boolean => {
  const normalizeList = (list: T[]) =>
    (Array.isArray(list) ? list : [])
      .map((item) => normalizer(item))
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  
  return (
    JSON.stringify(normalizeList(left)) ===
    JSON.stringify(normalizeList(right))
  );
};

export const buildClinicPharmaRatesPayload = (
  detailGroupId: number | null,
  pharmaGroups: PharmaGroupUi[],
) => ({
  detailGroupId,
  rates: pharmaGroups.flatMap((group) => {
    if (!Array.isArray(group.rows) || group.rows.length === 0) {
      return [];
    }

    return group.rows.map((row) => ({
      pharmaCompanyId: group.pharmaCompanyId,
      rate: Number(row.rate || 0),
      isApplied: resolveIsApplied(row),
      isRangeApplied: Boolean(row.rangeApplied),
      startYearMonth: formatYmWithDash(row.startMonth),
      endYearMonth: toEndYearMonthPayload(row.endMonth),
    }));
  }),
});

export const buildClinicDrugRatesPayload = (
  detailGroupId: number | null,
  drugGroupsByPharmaId: Record<string, DrugGroupUi[]>,
  pharmaGroups: PharmaGroupUi[],
) => {
  const pharmaGroupMap = new Map(
    pharmaGroups.map((group) => [group.id, group])
  );

  return {
    detailGroupId,
    rates: Object.entries(drugGroupsByPharmaId || {}).flatMap(
      ([pharmaId, groups]) => {
        if (!Array.isArray(groups) || pharmaId === 'all-pharma') return [];
        const pharmaGroup = pharmaGroupMap.get(pharmaId);

        return groups.flatMap((group) => {
          if (!Array.isArray(group.rows) || group.rows.length === 0) return [];
          
          return group.rows.map((row) => ({
            pharmaCompanyId:
              pharmaGroup?.pharmaCompanyId ?? group.pharmaCompanyId,
            drugId: group.drugId ?? null,
            rate: Number(row.rate || 0),
            isApplied: resolveIsApplied(row),
            isRangeApplied: Boolean(row.rangeApplied),
            startYearMonth: formatYmWithDash(row.startMonth),
            endYearMonth: toEndYearMonthPayload(row.endMonth),
          }));
        });
      }
    ),
  };
};

export const buildHospitalDrugRulesPayload = (
  detailGroupId: number | null,
  drugGroupsByPharmaId: Record<string, DrugGroupUi[]>,
) => {
  const allPharmaGroups = Array.isArray(drugGroupsByPharmaId?.['all-pharma'])
    ? drugGroupsByPharmaId['all-pharma']
    : [];

  return {
    detailGroupId,
    rules: allPharmaGroups.flatMap((group) => {
      if (!Array.isArray(group.rows) || group.rows.length === 0) return [];
      
      return group.rows.map((row) => ({
        drugId: group.drugId ?? null,
        rate: Number(row.rate || 0),
        isApplied: resolveIsApplied(row),
        isRangeApplied: Boolean(row.rangeApplied),
        startYearMonth: formatYmWithDash(row.startMonth),
        endYearMonth: toEndYearMonthPayload(row.endMonth),
        pharmaCompanyId: group.pharmaCompanyId != null ? Number(group.pharmaCompanyId) : null,
      }));
    }),
  };
};

// ===== 유효성 검사 =====

/**
 * 적용 기간 유효성 검사 - 한국어 에러 메시지 반환
 */
export const validateAppliedPeriods = (data: DetailDataUi): string | null => {
  // 제약사 수수료율 검사
  for (const group of data.pharmaGroups) {
    for (const row of group.rows || []) {
      if (!resolveIsApplied(row)) continue;
      
      if (!row.startMonth || !row.endMonth) {
        return `제약사: ${group.name} (기간 미설정)`;
      }
      
      // YYYYMM 형식으로 비교
      if (row.startMonth > row.endMonth) {
        return `제약사: ${group.name} (기간 역전)`;
      }
    }
  }

  // 의약품 수수료율 검사
  for (const groups of Object.values(data.drugGroupsByPharmaId || {})) {
    if (!Array.isArray(groups)) continue;
    
    for (const drugGroup of groups) {
      for (const row of drugGroup.rows || []) {
        if (!resolveIsApplied(row)) continue;
        
        if (!row.startMonth || !row.endMonth) {
          return `의약품: ${drugGroup.name} (기간 미설정)`;
        }
        
        if (row.startMonth > row.endMonth) {
          return `의약품: ${drugGroup.name} (기간 역전)`;
        }
      }
    }
  }

  return null;
};